import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus } from "react-feather";
import { ConnectorLogo } from "../../atoms";
import {
  Dropdown,
  InstanceListItem,
  type DropdownOption,
} from "../../molecules";
import { usePortalMenu } from "../../../hooks/usePortalMenu";
import { useConnectorRegistryQuery } from "../../../hooks/useConnectorApi";
import type { ConnectorRegistryRow } from "../../connectors";
import {
  AddInstanceButton,
  AddInstanceRow,
  Container,
  EmptyState,
  InstanceList,
  SectionRow,
} from "./ConnectorInstanceSidebar.styles";

const ICON_SIZE = 14;
const INSTANCE_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "2-digit",
  year: "numeric",
});

interface SidebarConnectorOption {
  catalogKey: string;
  displayName: string;
  iconUrl: string;
}

interface SidebarInstance {
  connectionId: string;
  name: string;
  lastSyncLabel: string;
  health: "healthy" | "warning" | "error";
}

const toInstanceHealth = (
  status: string | undefined,
): SidebarInstance["health"] => {
  const normalized = status?.toLowerCase().trim();
  if (
    normalized === "connected" ||
    normalized === "healthy" ||
    normalized === "success"
  ) {
    return "healthy";
  }
  if (
    normalized === "disconnected" ||
    normalized === "inactive" ||
    normalized === "failed" ||
    normalized === "error"
  ) {
    return "error";
  }
  return "warning";
};

const toLastSyncLabel = (value: string | undefined): string => {
  if (!value) return "Last sync : —";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Last sync : —";
  return `Last sync : ${INSTANCE_DATE_FORMATTER.format(parsed)}`;
};

const toSidebarInstances = (row: ConnectorRegistryRow): SidebarInstance[] =>
  (row.instances ?? []).map((instance) => ({
    connectionId: instance.connectorId,
    name: instance.connectionName,
    lastSyncLabel: toLastSyncLabel(instance.lastSync),
    health: toInstanceHealth(instance.status),
  }));

/**
 * Secondary sidebar shown on the Connection Detail page.
 *
 * Lists all instances of the currently-viewed connector and exposes a
 * dropdown to switch to other connectors the tenant has connected. Reads
 * route params directly (mounted by ResponsiveLayout in the secondary slot)
 * and navigates to keep URL ↔ selection in sync.
 *
 * "+ Add instance" is wired to a no-op for now — the add-instance flow is a
 * separate ticket per product direction.
 */
export const ConnectorInstanceSidebar: React.FC = () => {
  const { connectionId } = useParams<{ connectionId: string }>();
  const navigate = useNavigate();
  const { data: connectorRows = [], isFetching: isRegistryFetching } =
    useConnectorRegistryQuery();

  const [activeCatalogKey, setActiveCatalogKey] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const { triggerRef, menuRef, rect } = usePortalMenu<
    HTMLButtonElement,
    HTMLUListElement
  >({
    open: dropdownOpen,
    onOutsideClick: () => setDropdownOpen(false),
  });

  const connectedRows = useMemo(
    () => connectorRows.filter((row) => (row.instances?.length ?? 0) > 0),
    [connectorRows],
  );

  const connectorOptions: SidebarConnectorOption[] = useMemo(
    () =>
      connectedRows.map((row) => ({
        catalogKey: row.catalogKey,
        displayName: row.displayName,
        iconUrl: row.iconUrl,
      })),
    [connectedRows],
  );

  const instances = useMemo(() => {
    if (!activeCatalogKey) return [];
    const activeRow = connectedRows.find(
      (row) => row.catalogKey === activeCatalogKey,
    );
    if (!activeRow) return [];
    return toSidebarInstances(activeRow);
  }, [activeCatalogKey, connectedRows]);

  useEffect(() => {
    if (connectedRows.length === 0) {
      setActiveCatalogKey(null);
      return;
    }

    if (!connectionId) {
      const firstInstance = toSidebarInstances(connectedRows[0])[0];
      setActiveCatalogKey(connectedRows[0].catalogKey);
      if (firstInstance) {
        navigate(`/data-connectors/${firstInstance.connectionId}`, {
          replace: true,
        });
      }
      return;
    }

    const matchedRow = connectedRows.find(
      (row) =>
        row.connectionId === connectionId ||
        (row.instances ?? []).some(
          (instance) => instance.connectorId === connectionId,
        ),
    );
    if (matchedRow) {
      setActiveCatalogKey(matchedRow.catalogKey);
      return;
    }

    // No match yet — could be a freshly-created connection that the registry
    // hasn't surfaced. Don't replace the URL with a different connector; the
    // detail page handles "not found" on its own.
    if (isRegistryFetching) return;
    setActiveCatalogKey(null);
  }, [connectedRows, connectionId, navigate, isRegistryFetching]);

  const dropdownOptions: DropdownOption<string>[] = connectorOptions.map(
    (option) => ({
      value: option.catalogKey,
      label: option.displayName,
      icon: (
        <ConnectorLogo
          src={option.iconUrl}
          name={option.displayName}
          size="xs"
        />
      ),
    }),
  );

  const handleConnectorChange = (nextCatalogKey: string) => {
    setDropdownOpen(false);
    if (nextCatalogKey === activeCatalogKey) return;
    const nextRow = connectedRows.find(
      (row) => row.catalogKey === nextCatalogKey,
    );
    const nextInstances = nextRow ? toSidebarInstances(nextRow) : [];
    if (nextInstances.length === 0) return;
    setActiveCatalogKey(nextCatalogKey);
    navigate(`/data-connectors/${nextInstances[0].connectionId}`);
  };

  const handleInstanceClick = (instance: SidebarInstance) => {
    if (instance.connectionId === connectionId) return;
    navigate(`/data-connectors/${instance.connectionId}`);
  };

  const handleAddInstance = () => {
    if (!activeCatalogKey || !connectionId) return;
    navigate(
      `/data-connectors/${connectionId}?addInstance=1&catalogKey=${encodeURIComponent(
        activeCatalogKey,
      )}`,
    );
  };

  return (
    <Container aria-label="Connector instances">
      <SectionRow>
        <Dropdown
          value={activeCatalogKey}
          options={dropdownOptions}
          open={dropdownOpen}
          onToggle={() => setDropdownOpen((prev) => !prev)}
          onSelect={handleConnectorChange}
          triggerRef={triggerRef}
          menuRef={menuRef}
          menuRect={rect}
          ariaLabel="Switch connector"
          variant="ghost"
          size="md"
        />
      </SectionRow>

      <AddInstanceRow>
        <AddInstanceButton type="button" onClick={handleAddInstance}>
          <Plus size={ICON_SIZE} aria-hidden />
          <span>Add instance</span>
        </AddInstanceButton>
      </AddInstanceRow>

      {instances.length === 0 ? (
        <EmptyState>No instances yet.</EmptyState>
      ) : (
        <InstanceList>
          {instances.map((instance) => (
            <li key={instance.connectionId}>
              <InstanceListItem
                name={instance.name}
                lastSyncLabel={instance.lastSyncLabel}
                selected={instance.connectionId === connectionId}
                health={instance.health}
                onClick={() => handleInstanceClick(instance)}
              />
            </li>
          ))}
        </InstanceList>
      )}
    </Container>
  );
};

export default ConnectorInstanceSidebar;
